/**
 * The participant app: query cache, live updates, history router, and the chrome around the matched page (screens §3).
 * Team id = `authMe` membership for the route's cohort (ADR-0028); a page never resolves it itself.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { QueryCache, QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import type { Principal, SseEvent } from "@bsim/contracts";
import { ApiFailure, ApiProvider, useApi, useRoute, type StreamHealth } from "../api/context";
import { createApiClient, type ApiClient } from "../api/client";
import { useCohortStream } from "../api/stream";
import { authHref, safeReturnTo, SIGN_IN_PATH } from "../auth";
import { CoachProvider } from "../coach/CoachDrawer";
import { CoachThread } from "../coach/CoachThread";
import { openPages, routes, type PageDeps, type PageRoute } from "../router";
import { GxErrorBanner, GxWaiting } from "../theme/primitives";
import { ToastProvider } from "../theme/Toast";
import { ErrorBoundary } from "./ErrorBoundary";
import { Frame } from "./Frame";
import { matchPath, navigate, useLocation } from "./history";
import { landingPath } from "./landing";
import { rememberRun } from "./runs";
import { NotFound } from "./NotFound";
import { noteCalledIn } from "./calledIn";
import { noteStateChange } from "./reopened";

interface Match { readonly route: PageRoute; readonly params: Readonly<Record<string, string>> }

function matchRoute(pathname: string): Match | null {
  for (const route of routes) {
    const params = matchPath(route.path, pathname);
    if (params !== null) return { route, params };
  }
  return null;
}

const teamIdFor = (principal: Principal, cohortId: string | undefined): string | null =>
  principal.memberships.find((m) => m.cohortId === cohortId)?.teamId ?? null;

/** SSE events → cache invalidations; the polling fallback needs nothing more than the stream's health. */
export function LiveUpdates({ cohortId, facilitator, onHealth }: { readonly cohortId: string; readonly facilitator: boolean; readonly onHealth: (h: StreamHealth, lastEventAt: Date | null) => void }) {
  const queryClient = useQueryClient();
  const health = useCohortStream(
    cohortId,
    (e: SseEvent) => {
      noteStateChange(e);
      // The run's phase moved (a solo year resumed, a card halted it, results came in): the hub's own read and the team state the sidebar reads are stale together.
      if (e.type === "solo.phase_changed") void queryClient.invalidateQueries({ queryKey: ["soloResume"] });
      noteCalledIn(e);
      // The facilitator console reads many views (board, roster, room screen, finale, review queues): refresh them all.
      if (facilitator) { void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "authMe" && q.queryKey[0] !== "contentCatalog" }); return; }
      void queryClient.invalidateQueries({ queryKey: ["teamState"] });
      if (e.type === "round.released" || e.type === "round.rolled_back") void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "publicMarket" || q.queryKey[0] === "roundReport" });
      if (e.type === "finale.stage_changed") void queryClient.invalidateQueries({ predicate: (q) => ["publicMarket", "finaleReplay", "bankStory"].includes(String(q.queryKey[0])) });
      // A teammate's card answer changes the plan's starting point: the preview context is read again.
      if (e.type === "event.responded" || e.type === "event.delivered") void queryClient.invalidateQueries({ queryKey: ["getPreviewContext"] });
      if (e.type === "membership.changed") void queryClient.invalidateQueries({ queryKey: ["authMe"] });
    },
    { onUnhealthy: (lastEventAt) => onHealth("polling", lastEventAt) },
  );
  useEffect(() => { onHealth(health, null); }, [health, onHealth]);
  return null;
}

function Routed({ live, onHealth }: { readonly live: LiveState; readonly onHealth: (h: StreamHealth, lastEventAt: Date | null) => void }) {
  const api = useApi();
  const { pathname: urlPath, search } = useLocation();
  const me = useRoute("authMe", undefined, { staleTime: Infinity });
  const principal = me.data?.principal;
  const land = principal === undefined ? null : landingPath(principal);
  // "/" and a bare cohort path go to the person's landing page: decided here, so the landing page is drawn in the same frame (no blank one) while the address is replaced.
  const redirect = land !== null && matchRoute(urlPath) === null && (urlPath === "/" || /^\/c\/[^/]+\/?$/.test(urlPath)) ? land : null;
  const pathname = redirect ?? urlPath;
  const match = matchRoute(pathname);
  const cohortId = match?.params["cohortId"];
  // A new page opens at its top, not where the last page was scrolled to (a link to a place on the page, "#...", keeps its own).
  useEffect(() => { if (window.location.hash === "") document.scrollingElement?.scrollTo?.({ top: 0 }); }, [pathname]);

  // The run being looked at is the one this device opens next time (one person, many runs).
  const seen = principal === undefined ? undefined : principal.memberships.find((m) => m.cohortId === (cohortId ?? match?.params["runId"]));
  const seenBy = principal?.userId;
  const seenId = seen?.cohortId;
  useEffect(() => { if (seenId !== undefined && seenBy !== undefined) rememberRun(seenBy, seenId); }, [seenId, seenBy]);

  useEffect(() => { if (redirect !== null) navigate(redirect + search, { replace: true }); }, [redirect, search]);

  if (me.error?.result.status === 401) return <div className="gx-fullpage"><GxWaiting what="your account" height={96} /></div>; // the query cache is already sending them to sign in
  if (me.error !== null) return <div className="gx-fullpage"><GxErrorBanner error={me.error.result.error} /></div>;
  if (principal === undefined) return <div className="gx-fullpage"><GxWaiting what="your account" height={96} /></div>;
  if (match === null) {
    // Anything else is a page that does not exist, said in a line with a way back.
    return <NotFound backTo={land} />;
  }

  // The solo hub (`/solo/:runId`) is drawn in the same frame as the cohort pages: a solo run is a cohort, so its id is the cohort id.
  const frameCohort = cohortId ?? match.params["runId"];
  const deps: PageDeps = { api, principal, teamId: teamIdFor(principal, frameCohort) };
  const page = match.route.render(match.params, deps);
  return (
    <>
      {frameCohort === undefined || (cohortId === undefined && seen === undefined) ? null : <LiveUpdates cohortId={frameCohort} facilitator={principal.memberships.some((m) => m.cohortId === frameCohort && m.role === "facilitator")} onHealth={onHealth} />}
      {match.route.bare === true || frameCohort === undefined
        ? page
        : <Frame cohortId={frameCohort} page={cohortId === undefined ? "" : pathname.split("/")[3] ?? ""} deps={deps} health={live.health} lastSyncAt={live.lastSyncAt}>{page}</Frame>}
    </>
  );
}

interface LiveState { readonly health: StreamHealth; readonly lastSyncAt: Date | null }

function LiveShell({ api }: { readonly api: ApiClient }) {
  const [live, setLive] = useState<LiveState>({ health: "open", lastSyncAt: null });
  const onHealth = useCallback((health: StreamHealth, lastEventAt: Date | null) => setLive((prev) => ({ health, lastSyncAt: lastEventAt ?? prev.lastSyncAt })), []);
  return (
    <ApiProvider api={api} health={live.health}>
      <ToastProvider>
        <CoachProvider thread={(context) => <CoachThread context={context} />}><Routed live={live} onHealth={onHealth} /></CoachProvider>
      </ToastProvider>
    </ApiProvider>
  );
}

export function App({ api }: { readonly api?: ApiClient }) {
  const client = useMemo(() => api ?? createApiClient(), [api]);
  const open = openPages[useLocation().pathname.replace(/(.)\/$/, "$1")];
  return <ErrorBoundary>{open === undefined ? <SignedInApp api={client} /> : <>{open(client)}</>}</ErrorBoundary>;
}

/** Any signed-out read (no session, or one that has ended) sends the person to sign in, remembering the page they asked for. */
function toSignIn(): void {
  const { pathname, search } = window.location;
  if (pathname in openPages) return;
  navigate(authHref(SIGN_IN_PATH, safeReturnTo(pathname + search)), { replace: true });
}

/** A read that fails on our side (a 5xx) is tried once more; anything else, or a second failure, shows the banner. */
export const retryReadOnce = (count: number, error: Error): boolean => count < 1 && error instanceof ApiFailure && error.result.status >= 500;

function SignedInApp({ api }: { readonly api: ApiClient }) {
  const [queryClient] = useState(() => new QueryClient({
    queryCache: new QueryCache({ onError: (error) => { if (error instanceof ApiFailure && error.result.status === 401) toSignIn(); } }),
    // A read that fails on our side (a 5xx, e.g. results right after a release) is tried once more after 2 s before the banner shows.
    defaultOptions: { queries: { retry: retryReadOnce, retryDelay: 2000, refetchOnWindowFocus: false } },
  }));
  return <QueryClientProvider client={queryClient}><LiveShell api={api} /></QueryClientProvider>;
}
